import { useCallback, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Menu, X } from "lucide-react";
import { Link, NavLink } from "react-router";
import { TOPICS } from "@jipraks/shared";
import { useDismiss } from "@client/hooks/use-dismiss";
import { cn } from "@client/lib/cn";
import { authorsCopy } from "@client/locales/id/authors";
import { layoutCopy } from "@client/locales/id/layout";

const copy = layoutCopy.nav;

const LINK =
  "inline-flex min-h-11 items-center rounded-lg px-3 text-sm text-foreground-muted transition-colors duration-200 ease-expo-out hover:bg-surface hover:text-foreground";

// The mobile panel lists these, then every topic; the desktop bar adds a single topics entry.
const PRIMARY_LINKS = [
  { to: "/#katalog", label: copy.catalog },
  { to: "/blog", label: copy.blog },
  { to: "/authors", label: authorsCopy.nav },
];
const DESKTOP_LINKS = [...PRIMARY_LINKS, { to: "/topics/n8n", label: copy.topics }];

/** Inline links from md up; below md a slide-down panel (design.xml "Mobile Menu"). */
export function MainNav() {
  const [isOpen, setIsOpen] = useState(false);
  const panelRef = useRef<HTMLDivElement>(null);
  const toggleRef = useRef<HTMLDivElement>(null);
  const close = useCallback(() => setIsOpen(false), []);
  const dismissRefs = useMemo(() => [panelRef, toggleRef], []);
  useDismiss(dismissRefs, isOpen, close);

  return (
    <div ref={toggleRef} className="flex items-center">
      <nav aria-label={copy.mainLabel} className="hidden items-center gap-1 md:flex">
        {DESKTOP_LINKS.map((link) => (
          <NavLink key={link.to} to={link.to} className={({ isActive }) => cn(LINK, isActive && link.to !== "/#katalog" && "text-foreground")}>
            {link.label}
          </NavLink>
        ))}
      </nav>

      <button
        type="button"
        onClick={() => setIsOpen((open) => !open)}
        aria-expanded={isOpen}
        aria-label={isOpen ? copy.closeMenu : copy.openMenu}
        className="inline-flex size-11 items-center justify-center rounded-lg text-foreground-muted hover:bg-surface hover:text-foreground md:hidden"
      >
        {isOpen ? <X className="size-5" aria-hidden="true" /> : <Menu className="size-5" aria-hidden="true" />}
      </button>

      {/* Portaled: the header uses backdrop-filter, which would make it the containing block of
          a fixed child and clip the panel to the header height. */}
      {isOpen && createPortal(
        <div ref={panelRef} className="animate-fade-up fixed inset-x-0 top-16 bottom-0 z-40 overflow-y-auto border-t border-border-default bg-background-base px-4 pt-2 pb-6 [animation-duration:200ms] md:hidden">
          <nav aria-label={copy.mainLabel} className="flex flex-col">
            {PRIMARY_LINKS.map((link) => (
              <Link key={link.to} to={link.to} onClick={close} className={cn(LINK, "text-base")}>
                {link.label}
              </Link>
            ))}
            <p className="mt-4 px-3 font-mono text-xs tracking-widest text-foreground-muted uppercase">{copy.topics}</p>
            <div className="mt-1 grid grid-cols-2">
              {TOPICS.map((topic) => (
                <Link key={topic.slug} to={`/topics/${topic.slug}`} onClick={close} className={LINK}>
                  {topic.name}
                </Link>
              ))}
            </div>
          </nav>
        </div>,
        document.body,
      )}
    </div>
  );
}
